import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoggerService } from '../services/logger.service';
import { Project } from '../models/project.model';
import { AuthService } from '../auth/auth.service';
import { PagedResponse } from '../models/pagination.model';
import { ProjectListItem, ProjectListQueryParams } from '../models/project-list.model';

@Injectable({
  providedIn: 'root'
})
export class ProyectoService {

  constructor(private http: HttpClient, private logger: LoggerService, private authService: AuthService) {}

  getUltimosProyectos(userId: number): Observable<any[]> {
      return this.http.get<any[]>(`${environment.api_service_project}/user/${userId}/last`);
  }

  /**
   * NUEVO (2026-05-19): Listado paginado, filtrable y ordenable de proyectos.
   *
   * Consume el endpoint optimizado `GET /projects/list`, que devuelve un DTO
   * ligero (sólo los campos que renderiza el directorio + agregados de horas).
   * NO carga estimaciones, ejecuciones, sistemas ni tecnologías anidadas; para
   * el grafo completo de un proyecto, usar `getProyectoById()`.
   *
   * Comportamiento del backend (resumen):
   *  - `pageSize` se capa a 100 silenciosamente.
   *  - `sort` aplica whitelist (`name`, `creationdate`); valores fuera de la
   *    lista caen al default `creationdate:desc`.
   *  - `q` se sanitiza (se neutralizan los wildcards `%` y `_`).
   *  - Orden secundario por `id` garantiza paginación estable.
   *
   * @param params Filtros, paginación y orden. Todos los campos son opcionales.
   * @returns Observable con la respuesta paginada tipada.
   */
  getProyectosPaginated(
    params: ProjectListQueryParams = {}
  ): Observable<PagedResponse<ProjectListItem>> {
    let httpParams = new HttpParams();

    // Omitimos parámetros vacíos: HttpParams serializaría `?key=` y ensuciaría
    // la URL. Solo enviamos los que tienen valor real.
    if (params.page != null)                  httpParams = httpParams.set('page', String(params.page));
    if (params.pageSize != null)              httpParams = httpParams.set('pageSize', String(params.pageSize));
    if (params.sort)                          httpParams = httpParams.set('sort', params.sort);
    if (params.q && params.q.trim())          httpParams = httpParams.set('q', params.q.trim());
    if (params.societyId)                     httpParams = httpParams.set('societyId', params.societyId);
    if (params.typeId)                        httpParams = httpParams.set('typeId', params.typeId);
    if (params.systemId)                      httpParams = httpParams.set('systemId', params.systemId);

    return this.http.get<PagedResponse<ProjectListItem>>(
      `${environment.api_service_project}/list`,
      { params: httpParams }
    );
  }

  getProyectoById(id: string): Observable<any> {
    return this.http.get<any>(`${environment.api_service_project}/${id}`);
  }

  saveProject(project: Project): Observable<any> {
    this.logger.log("ProyectoService.saveProject");

    const preparedProject = this.prepareProjectForBackend(project);
    const headers = { 'Content-Type': 'application/json' };

    if (project.id) {
      const userJson = this.authService.getCurrentUser();
      const body = { data: preparedProject, userid: userJson?.id };
      return this.http.put(`${environment.api_service_project}/${project.id}`, body, { headers });
    } else {
      return new Observable<any>(observer => {
        observer.error('El proyecto no existe o no tiene un ID válido para actualizar.');
      });
    }
  }

  bulkUpdateExecutions(projectId: string, payload: { updates: any[], deletions: string[] }): Observable<any> {
    this.logger.log("ProyectoService.bulkUpdateExecutions");
    return this.http.put(`${environment.api_service_project}/${projectId}/executions/bulk`, payload);
  }

  deleteProject(id: string): Observable<any> {
    this.logger.log("ProyectoService.deleteProject");
    return this.http.delete(`${environment.api_service_project}/${id}`);
  }

  private prepareProjectForBackend(project: Project): any {
    return {
      id: project.id,
      name: project.name,
      description: project.description,
      startdate: project.startdate,
      enddate: project.enddate,
      typeid: project.type?.id,
      departmentid: project.department?.id,
      systemIds: (project.systems || []).map(s => s.id)
    };
  }

  setFechaActual(): any {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

}